<template>
  <div class="flex flex-col">
    <div v-if="isSitterDeativated">
      <Button variant="outlined" value="Mark as not a fit" class="w-full" @click="moveToNotAFit" />
    </div>
    <div v-else-if="isApplicationInNotAFit">
      <Button value="Move to received" size="small" variant="outlined" class="w-full" @click="moveToReceived" />
    </div>
    <div v-else-if="account.isBasic()">
      <Button class="w-full" size="medium" :to="redirectReplyURL" @click="setRecentlyViewedProviderId">
        <LockIcon v-if="account.isBasic()" class="w-18 pr-4" color="white" /><span>Reply</span>
      </Button>
      <Button variant="outlined" size="medium" value="Mark as not a fit" class="w-full mt-16" @click="moveToNotAFit" />
      <div class="flex mt-16">
        <HelpOutlineIcon class="text-grey-clickable mr-4" :width="24" :height="24" />
        <p class="text-caption mt-2">{{ displayName }} will not be notified when marked as "not a fit" and this can be undone</p>
      </div>
    </div>
    <div v-else>
      <BaseButtons
        v-if="isBaseButtonsState"
        @click-interested="setInterestedComponentState"
        @click-not-a-fit="setNotAFitComponentState"
      />
      <InterestedMessageBox v-else-if="isInterestedMessageBoxState" @close="setBaseButtonsComponentState" />
      <NotAFitMessageBox v-else-if="isNotAFitMessageBoxState" @close="setBaseButtonsComponentState" />
      <HiringTip v-if="!isNotAFitMessageBoxState" class="hidden md:block" />
    </div>
  </div>
</template>

<script lang="ts">
import Button from '@/components/button/Button.vue';
import { Account } from '@/models/account';
import LockIcon from '@/graphics/icons/LockIcon.vue';
import HelpOutlineIcon from '@/graphics/icons/HelpOutlineIcon.vue';
import InterestedMessageBox from './MessageBoxes/InterestedMessageBox.vue';
import NotAFitMessageBox from './MessageBoxes/NotAFitMessageBox.vue';
import { UPDATE_JOB_APPLICATION_DISMISSAL } from '@/graphql/applications/mutations';
import BaseButtons from './MessageBoxes/BaseButtons.vue';
import HiringTip from './HiringTip.vue';
import { setRecentlyViewedProviderId } from '@/lib/recentlyViewedProfile';

type ComponentStates = 'BASE_BUTTONS' | 'INTERESTED_MESSAGE_BOX' | 'NOT_A_FIT_MESSAGE_BOX';

export default {
  name: 'ParentHasNotReplied',
  components: { Button, LockIcon, HelpOutlineIcon, InterestedMessageBox, NotAFitMessageBox, BaseButtons, HiringTip },
  inject: {
    navigateToNextApplication: { default: () => {} },
  },
  data(): { currentComponentState: ComponentStates } {
    return {
      currentComponentState: 'BASE_BUTTONS',
    };
  },
  computed: {
    isApplicationInNotAFit(): boolean {
      return this.$store.getters['applications/applicationDetails'].parentInterestStatus === 'NOT_A_FIT';
    },
    isSitterDeativated(): boolean {
      return this.$store.getters['applications/applicationDetails'].sitter?.state !== 'active';
    },
    isBaseButtonsState(): boolean {
      return this.currentComponentState === 'BASE_BUTTONS';
    },
    isInterestedMessageBoxState(): boolean {
      return this.currentComponentState === 'INTERESTED_MESSAGE_BOX';
    },
    isNotAFitMessageBoxState(): boolean {
      return this.currentComponentState === 'NOT_A_FIT_MESSAGE_BOX';
    },
    account(): Account {
      return this.$store.getters['account/getAccount'];
    },
    redirectReplyURL(): string | undefined {
      if (this.account.isBasic()) return '/upgrade/pricing?source=applications';
      return undefined;
    },
    displayName(): string {
      return this.$store.getters['applications/applicationDetails'].sitter?.displayName || 'Applicant';
    },
  },
  methods: {
    setRecentlyViewedProviderId(): void {
      if (this.account.isBasic()) {
        const sitterId = this.$store.getters['applications/applicationDetails']?.sitter?.id;
        if (sitterId) {
          setRecentlyViewedProviderId(sitterId);
        }
      }
    },
    setBaseButtonsComponentState(): void {
      this.currentComponentState = 'BASE_BUTTONS';
    },
    setInterestedComponentState(): void {
      this.currentComponentState = 'INTERESTED_MESSAGE_BOX';
    },
    setNotAFitComponentState(): void {
      this.currentComponentState = 'NOT_A_FIT_MESSAGE_BOX';
    },
    moveToNotAFit(): void {
      this.$apollo
        .mutate({
          mutation: UPDATE_JOB_APPLICATION_DISMISSAL,
          variables: {
            applicationUuids: [this.$store.getters['applications/applicationDetails'].id],
            dismiss: true,
          },
        })
        .then(() => {
          this.$store.dispatch('applications/moveAppFromReceivedToNotAFit');
          this.$store.dispatch('applications/showMessageSentNotification', 'Applicant moved to not a fit');
          this.navigateToNextApplication();
        })
        .catch((error: any) => {
          console.error('GraphQL query error:', error);
        });
    },
    moveToReceived(): void {
      this.$apollo
        .mutate({
          mutation: UPDATE_JOB_APPLICATION_DISMISSAL,
          variables: {
            applicationUuids: [this.$store.getters['applications/applicationDetails'].id],
            dismiss: false,
          },
        })
        .then(() => {
          this.$store.dispatch('applications/moveAppFromNotAFitToReceived');
          this.$store.dispatch('applications/showMessageSentNotification', 'Applicant moved to received');
          this.navigateToNextApplication();
        })
        .catch((error: any) => {
          console.error('GraphQL query error:', error);
        });
    },
  },
};
</script>
